import { ASSISTANT, CHOICE, CONTACT, FORM, LOOKUP, SUBMIT } from './nodes.js';
import Contact from '../Contact.js';
import Services from '../Services.js';

/*
 * The escalation sub-flow, shared by all five brands.
 *
 * Runs at the END of a flow, so someone who finds their answer in a document
 * never hands over a name or an email to read an article.
 */

export const ESCALATION_ENTRY = 'channel';

// True when the host passed a dealership in, so nothing needs looking up.
const hasDealership = () => Boolean(Services.dealership()?.id);

/*
 * Which escalations the assistant is allowed to take, by reason.
 *
 * NOT EVERY ESCALATION IS A CONVERSATION. Troubleshooting is: the customer has
 * a symptom, GRACI has the manuals, and talking it through is genuinely the
 * fastest route. Replacing hardware is not. That one ends in equipment leaving
 * a warehouse, a warranty being read, and somebody being told what they will
 * and will not be charged, and an assistant that sounds confident about any of
 * those does real damage whether or not it is right. The same goes for booking
 * a service visit and for getting somebody back into a locked account, where
 * an identity has to be checked against the network before anything moves.
 *
 * `people` means the choice is not offered at all rather than offered and then
 * walked back. Somebody who picks the assistant and is told two screens later
 * that it cannot help them has been sent the long way round for nothing.
 *
 * EVERY REASON IN EVERY TREE MUST APPEAR HERE. engine/validate.js fails a tree
 * carrying a reason this map has not heard of, because the alternative is a
 * default, and either default is wrong: assume `any` and a new replacement
 * flow quietly gains an assistant, assume `people` and a new troubleshooting
 * flow quietly loses one. Neither shows up until somebody notices in the wild.
 */
export const REASON_CHANNELS = {
  // Talking it through is the point.
  chirp_undiagnosed: 'any',
  chirping: 'any',
  cleaning: 'any',
  cleaning_unresolved: 'any',
  comm_link_offline: 'any',
  comm_link_setup: 'any',
  battery_question: 'any',
  identify_product: 'any',
  other: 'any',
  general: 'any',
  // Hardware leaves a warehouse and money is discussed.
  battery_dead: 'people',
  battery_early_replacement: 'people',
  battery_replacement: 'people',
  bracket_replacement: 'people',
  end_of_life_replacement: 'people',
  guarantee_replacement: 'people',
  low_battery_replacement: 'people',
  modern_replacement: 'people',
  power_replacement: 'people',
  service_life_replacement: 'people',
  // A visit to book, and an identity to confirm.
  moving_service: 'people',
  app_account: 'people'
};

/*
 * Unknown reasons fall to people, deliberately. The validator catches them
 * long before this does, so reaching here means a reason arrived from
 * somewhere that was never checked, and a person is the safe answer to that.
 */
export const allowsAssistant = reason => REASON_CHANNELS[reason] === 'any';
export const ESCALATION_NODES = {
  /*
   * Call, or put it in writing. Asked before anything is collected.
   * The whole step vanishes when there is no number to give.
   */
  channel: {
    guard: [{
      goto: 'chat',
      when: facts => facts.channel === 'chat' && allowsAssistant(facts.reason)
    }, {
      goto: 'identity',
      when: (facts, brand) => Contact.forBrand(brand) === null
    }, {
      goto: 'callDetails',
      when: facts => facts.channel === 'call'
    }, {
      goto: 'identity',
      when: facts => facts.channel === 'write'
    }],
    help: 'Either way reaches the same people. Writing carries everything you have already told us, so nothing gets repeated.',
    options: [{
      icon: 'stars',
      label: 'I would rather chat with GRACI AI',
      next: 'chat',
      when: (facts, brand) => brand?.assistant === true && allowsAssistant(facts.reason)
    }, {
      icon: 'envelope-fill',
      label: 'I would rather send it in writing',
      next: 'identity'
    }, {
      icon: 'telephone-fill',
      label: 'I would rather call',
      next: 'callDetails'
    }],
    prompt: 'How would you like to reach us?',
    type: CHOICE
  },
  /*
   * Contact details, so the lookup can find their dealership and give them that
   * number instead of the home office. No message box: they are about to say
   * it out loud. Skipped when the host already passed a dealership in.
   */
  callDetails: {
    guard: [{
      goto: 'call',
      when: () => hasDealership()
    }],
    fields: [{
      autoComplete: 'given-name',
      id: 'firstName',
      kind: 'text',
      label: 'First name',
      required: true
    }, {
      autoComplete: 'family-name',
      id: 'lastName',
      kind: 'text',
      label: 'Last name',
      required: true
    }, {
      autoComplete: 'email',
      id: 'email_address',
      kind: 'email',
      label: 'Email address',
      required: true
    }, {
      autoComplete: 'tel',
      id: 'phone_number',
      kind: 'tel',
      label: 'Phone number',
      required: true
    }],
    help: 'We use this to find the dealership on your account, so you reach the people who installed your system rather than a general line.',
    next: 'callLookup',
    title: `Let's get you connected`,
    type: FORM
  },
  /*
   * Skipped when the answer is already known: brands that only ever reach the
   * home office, and replicated sites where the dealership is the context.
   */
  callLookup: {
    guard: [{
      goto: 'call',
      when: (facts, brand) => brand.escalation !== 'dealership_then_home_office' || hasDealership()
    }],
    next: 'call',
    type: LOOKUP
  },
  // Terminal, but not a dead end: the renderer offers the form as a way on.
  call: {
    next: 'message',
    type: CONTACT
  },
  // GRACI, seeded with everything the tree already established. Also not a
  // dead end; it carries the same way on to the form that the phone screen does.
  chat: {
    next: 'identity',
    type: ASSISTANT
  },
  identity: {
    fields: [{
      autoComplete: 'given-name',
      id: 'firstName',
      kind: 'text',
      label: 'First name',
      required: true
    }, {
      autoComplete: 'family-name',
      id: 'lastName',
      kind: 'text',
      label: 'Last name',
      required: true
    }, {
      autoComplete: 'email',
      id: 'email_address',
      kind: 'email',
      label: 'Email address',
      required: true
    }, {
      autoComplete: 'tel',
      id: 'phone_number',
      kind: 'tel',
      label: 'Phone number',
      required: true
    }, {
      id: 'message',
      kind: 'textarea',
      label: 'What is happening?',
      placeholder: 'Anything you can tell us in your own words.  Optional, but it usually saves a round of questions.',
      required: false
    }],
    action: 'Send this',
    help: `Please include your name and contact information below. We'll automatically include anything you've told us up until this point. You can attach any additional information using the "What is happening?" field below.`,
    next: 'submit',
    title: 'Your details',
    type: FORM
  },
  // Assumes callDetails collected who they are; ask in full when it was skipped.
  message: {
    guard: [{
      goto: 'identity',
      when: (facts, brand, answers) => !answers?.email_address
    }],
    fields: [{
      id: 'message',
      kind: 'textarea',
      label: 'What is happening?',
      placeholder: 'Anything you can tell us in your own words.  Optional, but it usually saves a round of questions.',
      required: false
    }],
    action: 'Send this',
    help: `We'll include everything you have already told us, so there is no need to repeat any of it.`,
    next: 'submit',
    title: 'Anything to add?',
    type: FORM
  },
  // Terminal for the sub-flow. The renderer submits here; the engine stops.
  submit: {
    type: SUBMIT
  }
};

/*
 * Who an escalation reaches.
 *
 * Declared once, per brand, in the registry, so no node in any tree decides
 * this. A node says "escalate, and here is why"; this resolves the destination.
 *
 * `home_office` is a ROLE. The person answering it today is not the person
 * answering it in two years.
 */
export const resolveTarget = (brand, lookup) => {
  /*
   * On a replicated website the dealership is known before the customer
   * arrives, so there is nothing to resolve: it is theirs. This is what makes
   * the same tree route to a dealership on one property and to the home office on
   * the other without either of them holding a rule about it.
   */
  let owner = Services.dealership();
  if (owner?.id) {
    return {
      dealership: owner,
      role: 'dealership'
    };
  }
  if (brand?.escalation === 'dealership_then_home_office' && lookup?.dealership) {
    return {
      dealership: lookup.dealership,
      role: 'dealership'
    };
  }
  return {
    dealership: null,
    role: 'home_office'
  };
};